/**
 * Timezone utilities for slot/booking times.
 *
 * MODEL (two frames):
 *  - STORAGE/ANCHOR is the activity's (venue) wall-clock: `startTime`/`endTime`
 *    are minutes-since-midnight in the activity's IANA zone, `date` is the
 *    venue-local calendar date. A recurring "5pm" stays 5pm at the venue across
 *    venue DST. The server resolves each occurrence to an ABSOLUTE INSTANT
 *    (`startAt`/`endAt` on availability, `eventStartAt`/`eventEndAt` on bookings).
 *  - DISPLAY is the VIEWER's browser timezone: we render the absolute instant in
 *    `Intl`'s local zone, so everyone sees the moment in their own time. A class
 *    a Seattle pro entered as "5pm" is stored as the matching Paris wall-clock and
 *    shows 5pm to the Seattle pro, ~2am to a Paris client. Across Paris DST the
 *    Paris client's time stays put; the Seattle pro's shifts.
 *  - AUTHORING input is the pro's browser time → converted to the venue wall-clock
 *    on save (`browserWallClockToVenue`). For a local pro (browser == venue) it is
 *    a no-op; only a cross-zone author is shifted.
 *
 * Prefer the instant helpers (`formatInstantTime`, `instantLocalDay`) fed by the
 * server's `startAt`/`eventStartAt`. The legacy minutes-based helpers remain for
 * sites that don't yet carry the instant; they take the venue `timezone` to
 * resolve first, then render in the browser zone.
 *
 * No Luxon import (kept out of the web bundle, ~26 KiB): everything below uses
 * the native `Intl.DateTimeFormat`.
 */

/** Default activity (venue) timezone when none is supplied (Métropole). */
export const ACTIVITY_TIMEZONE = 'Europe/Paris';

/** The viewer's browser IANA timezone (e.g. 'Europe/Paris', 'America/Los_Angeles'). */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export { timezoneCity } from '@ui/lib/time';

/** Format an absolute instant (epoch ms) as `HH:mm` in the viewer's browser zone. */
export function formatInstantTime(epochMs: number, timeZone: string = browserTimezone()): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).format(new Date(epochMs));
}

/**
 * The browser-zone calendar day an instant falls on, as a local-midnight Date
 * (its local Y/M/D are the day to bucket the occurrence under in the picker).
 */
export function instantLocalDay(epochMs: number, timeZone: string = browserTimezone()): Date {
  // Render the instant's wall-clock parts in `timeZone`, then build a local Date.
  const p: Record<string, number> = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date(epochMs))) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  return new Date(p.year, p.month - 1, p.day);
}

/** The `YYYY-MM-DD` calendar date an instant falls on in a given/browser zone. */
export function instantLocalDateString(epochMs: number, timeZone: string = browserTimezone()): string {
  return instantWallClock(epochMs, timeZone).date;
}

/** Wall-clock parts (date + minutes-of-day) of an instant rendered in a given/browser zone. */
export function instantWallClock(
  epochMs: number,
  timeZone: string = browserTimezone()
): { date: string; minutes: number } {
  const p: Record<string, number> = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(new Date(epochMs))) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  const date = `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
  return { date, minutes: p.hour * 60 + p.minute };
}

/**
 * Convert a wall-clock the pro typed in THEIR browser zone into the venue's
 * wall-clock for storage. `date` is the YYYY-MM-DD the pro picked, `startMinutes`/
 * `endMinutes` the minutes they typed (browser-local). Returns the venue-local
 * date + minutes to store (anchored to `venueTimezone`), handling the date
 * rollover (5pm Seattle → 2am Paris next day) and re-normalizing a cross-midnight
 * end to `start + duration`. When the browser zone equals the venue zone this is
 * the identity.
 */
export function browserWallClockToVenue(
  date: string,
  startMinutes: number,
  endMinutes: number,
  venueTimezone: string,
  browserTz: string = browserTimezone()
): { date: string; startTime: number; endTime: number } {
  const durationMin = (((endMinutes - startMinutes) % 1440) + 1440) % 1440 || endMinutes - startMinutes;
  const startInstant = slotInstantMs(date, startMinutes, browserTz);
  const venue = instantWallClock(startInstant, venueTimezone);
  return { date: venue.date, startTime: venue.minutes, endTime: venue.minutes + durationMin };
}

/**
 * UTC offset (ms) of a timezone at a given absolute instant, via native Intl —
 * no timezone library. Positive east of UTC (Europe/Paris: +1h winter, +2h DST).
 */
function tzOffsetMs(utcMs: number, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const p: Record<string, number> = {};
  for (const part of dtf.formatToParts(new Date(utcMs))) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - utcMs;
}

/**
 * Convert a stored time slot to its display time. Stored minutes ARE the
 * activity-local (Europe/Paris) wall-clock, so display is the identity — the
 * value is rendered verbatim. Kept as a function (rather than removing ~25 call
 * sites) so the whole display surface flips at this single chokepoint.
 *
 * `endTime > 1440` still denotes a window crossing local midnight; it is left
 * as-is and normalized for display by `formatTimeFromMinutes`.
 */
export function convertUtcTimeSlotToLocal(
  startTime: number,
  endTime: number,
  date: Date
): {
  startTime: number;
  endTime: number;
  localDate: Date;
  dateShifted: boolean;
} {
  // The stored date arrives as a UTC-midnight Date; return a local-midnight Date
  // with the SAME calendar day so callers' local getters read the stored date.
  const localDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return { startTime, endTime, localDate, dateShifted: false };
}

/**
 * Absolute instant (epoch ms) at which an occurrence starts, resolving the
 * stored activity-local wall-clock through the activity's zone. `date` is the
 * occurrence's activity-local slot_date (YYYY-MM-DD); `startMinutes` is the
 * stored `startTime`. `timezone` defaults to Métropole; pass the activity's zone
 * for DOM-TOM accuracy. Used to bucket occurrences by day and to drop slots that
 * have already started (cosmetic — the server is authoritative).
 */
export function slotInstantMs(date: string, startMinutes: number, timezone: string = ACTIVITY_TIMEZONE): number {
  const [year, month, day] = date.split('-').map(Number);
  const dayOffset = Math.floor(startMinutes / 1440);
  const mod = startMinutes - dayOffset * 1440;
  // Naive UTC guess for the wall-clock, then subtract the zone offset. A second
  // iteration corrects the case where the guess and the true instant fall on
  // opposite sides of a DST transition (the offset at the guess differs from the
  // offset at the real instant). This makes it agree with the domain resolver for
  // every time except the ~1h gap/fold window itself, where it stays within an
  // hour — acceptable since this drives only cosmetic past-filtering/bucketing.
  const guess = Date.UTC(year, month - 1, day + dayOffset, Math.floor(mod / 60), mod % 60);
  const o1 = tzOffsetMs(guess, timezone);
  const o2 = tzOffsetMs(guess - o1, timezone);
  return guess - o2;
}

/**
 * The viewer-local calendar day an occurrence falls on, derived from its true
 * instant ({@link slotInstantMs}). Returned as a local-midnight Date so its
 * local Y/M/D are the day the occurrence should appear under in the picker.
 */
export function slotLocalDay(utcDate: string, utcStartMinutes: number, timezone: string = ACTIVITY_TIMEZONE): Date {
  const d = new Date(slotInstantMs(utcDate, utcStartMinutes, timezone));
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Convert a slot the author entered into the stored representation. The author
 * (in France) types Europe/Paris wall-clock and picks a Paris-local date; we
 * store both verbatim (floating local). This is the identity counterpart of
 * `convertUtcTimeSlotToLocal` — no offset is applied. The only normalization is
 * rolling an end-before-start onto the next day (`+1440`) for a window that
 * crosses local midnight.
 */
export function convertLocalTimeSlotToUtc(
  localStartTime: number,
  localEndTime: number,
  localDate: Date
): {
  startTime: number;
  endTime: number;
  utcDate: Date;
  dateShifted: boolean;
} {
  // Preserve the picked calendar day as a UTC-midnight Date so callers that do
  // `utcDate.toISOString().slice(0,10)` get the stored YYYY-MM-DD.
  const utcDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate()));

  let endTime = localEndTime;
  if (endTime < localStartTime) {
    endTime = endTime + 1440;
  }

  return {
    startTime: localStartTime,
    endTime,
    utcDate,
    dateShifted: false
  };
}

/**
 * Convert time in minutes to formatted time string (HH:MM)
 * @param minutes Minutes since midnight
 * @returns Formatted time string
 */
export function formatTimeFromMinutes(minutes: number): string {
  // Handle case where minutes might be negative (due to timezone conversion)
  // or exceed 24 hours (also due to timezone conversion)
  let adjustedMinutes = minutes;

  // Normalize to 0-1439 range (24 hours)
  if (adjustedMinutes < 0) {
    adjustedMinutes = ((adjustedMinutes % 1440) + 1440) % 1440;
  } else if (adjustedMinutes >= 1440) {
    adjustedMinutes = adjustedMinutes % 1440;
  }

  const hours = Math.floor(adjustedMinutes / 60);
  const mins = adjustedMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Duration of a slot window in minutes, robust to UTC-midnight crossing.
 *
 * `startTime`/`endTime` are minutes-of-day. When a window crosses midnight the
 * stored `endTime` is either normalized as `start + duration` (i.e. > 1440) or,
 * for some legacy rows, left as the raw next-day minute (so `end < start`,
 * yielding a negative naive difference like the infamous `-1350m`). Wrapping
 * the difference into [0, 1440) yields the correct positive duration in all
 * cases (e.g. 23:00 → 00:30 ⇒ 90).
 */
export function slotDurationMinutes(startTime: number, endTime: number): number {
  return (((endTime - startTime) % 1440) + 1440) % 1440;
}

/**
 * Get the user's timezone identifier
 * @returns Timezone identifier (e.g., 'America/New_York', 'Europe/London')
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Convert a slot's time windows from UTC to local time
 * @param slotRule Slot with UTC time windows
 * @param targetDate The date for which to convert the times
 * @returns Slot with local time windows
 */
export function convertSlotTimeSlots(
  slotRule: {
    timeSlots: Array<{
      startTime: number;
      endTime: number;
      price: number;
    }>;
    startDate: string; // ISO format YYYY-MM-DD
  },
  targetDate?: Date
): {
  timeSlots: Array<{
    startTime: number;
    endTime: number;
    price: number;
  }>;
  localDate: Date;
  hasDateShifts: boolean;
} {
  // Parse the slot start date or use target date
  const slotDate = targetDate || new Date(slotRule.startDate + 'T00:00:00.000Z');

  let hasDateShifts = false;
  const convertedTimeSlots = slotRule.timeSlots.map((slot) => {
    const converted = convertUtcTimeSlotToLocal(slot.startTime, slot.endTime, slotDate);

    if (converted.dateShifted) {
      hasDateShifts = true;
    }

    return {
      startTime: converted.startTime,
      endTime: converted.endTime,
      price: slot.price
    };
  });

  return {
    timeSlots: convertedTimeSlots,
    localDate: slotDate,
    hasDateShifts
  };
}

/**
 * Format a date and time for display in local timezone
 * @param utcMinutes Minutes since midnight in UTC
 * @param utcDate UTC date
 * @returns Formatted local date and time string
 */
export function formatLocalDateTime(utcMinutes: number, utcDate: Date): string {
  const utcHours = Math.floor(utcMinutes / 60);
  const utcMins = utcMinutes % 60;

  const utcDateTime = new Date(
    Date.UTC(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate(), utcHours, utcMins, 0, 0)
  );

  // Convert to local time and format
  return utcDateTime.toLocaleString();
}

/**
 * Format a number of minutes-since-midnight as a clock time string.
 *
 * Kept under the legacy name `formatTimeWithLuxon` so callers don't need to be
 * updated — but the implementation is now Luxon-free. Every current caller
 * uses the default `'HH:mm'` format; the optional second arg supports ONLY
 * the Luxon-style `H`, `HH`, `m`, `mm` tokens that callers actually use today.
 *
 * Any other token (`a`, `EEEE`, `LLLL`, …) is returned verbatim as a literal
 * character — there is no Intl fallback. If you need richer formatting, add
 * it explicitly here or build an `Intl.DateTimeFormat` at the call site.
 */
export function formatTimeWithLuxon(minutes: number, format: string = 'HH:mm'): string {
  let adjustedMinutes = minutes;

  if (adjustedMinutes < 0) {
    adjustedMinutes = ((adjustedMinutes % 1440) + 1440) % 1440;
  } else if (adjustedMinutes >= 1440) {
    adjustedMinutes = adjustedMinutes % 1440;
  }

  const hours = Math.floor(adjustedMinutes / 60);
  const mins = adjustedMinutes % 60;

  // Hot path: HH:mm (every caller as of this commit).
  if (format === 'HH:mm') {
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // Cold path: only the four tokens below are substituted. Any other letters
  // in the format string are passed through unchanged.
  return format
    .replace(/HH/g, hours.toString().padStart(2, '0'))
    .replace(/H/g, hours.toString())
    .replace(/mm/g, mins.toString().padStart(2, '0'))
    .replace(/m/g, mins.toString());
}

/**
 * Convert API availability data to display-ready time slots with timezone conversion
 * This function handles the complex conversion from UTC API data to local display format
 * @param availabilityData Raw API data for a specific day, optionally enriched with slotName
 * @param selectedDate The date for which to convert the times
 * @returns Array of time slots ready for UI display
 */
export function convertApiSlotsToDisplaySlots(
  availabilityData: Array<{
    slotId: string;
    /** Occurrence UTC slot_date (YYYY-MM-DD) — server identity + display anchor. */
    utcDate: string;
    slotName?: string;
    timeSlots: Array<{
      startTime: number;
      endTime: number;
      /** Absolute instants (epoch ms) from the API — render these in browser time. */
      startAt?: number;
      endAt?: number;
      price: number;
      availableNumberOfPeople: number;
      acceptsCash?: boolean;
      acceptsOnline?: boolean;
    }>;
  }>
): Array<{
  id: string;
  time: string;
  price: string;
  available: boolean;
  metadata: {
    utcStartTime: number;
    utcEndTime: number;
    utcDate: string;
    /** Absolute start instant (epoch ms), venue-anchored — for browser-time past-filtering. */
    startAt?: number;
    slotId: string;
    slotName?: string;
    acceptsCash?: boolean;
    acceptsOnline?: boolean;
  };
}> {
  const slots: Array<{
    id: string;
    time: string;
    price: string;
    available: boolean;
    metadata: {
      utcStartTime: number;
      utcEndTime: number;
      utcDate: string;
      startAt?: number;
      slotId: string;
      slotName?: string;
      acceptsCash?: boolean;
      acceptsOnline?: boolean;
    };
  }> = [];

  availabilityData.forEach((slotAvailability) => {
    // Anchor the local-time conversion on the occurrence's true UTC slot_date so
    // the displayed time matches the real instant (and the local day it's
    // bucketed under), regardless of the viewer's offset.
    const utcDateObj = new Date(`${slotAvailability.utcDate}T00:00:00.000Z`);
    slotAvailability.timeSlots.forEach((apiSlot) => {
      // Render in the VIEWER's browser time from the server-resolved absolute
      // instants (venue-anchored). Fall back to the legacy venue-verbatim render
      // for any payload that predates startAt/endAt.
      let displayTime: string;
      if (apiSlot.startAt !== undefined && apiSlot.endAt !== undefined) {
        displayTime = `${formatInstantTime(apiSlot.startAt)} - ${formatInstantTime(apiSlot.endAt)}`;
      } else {
        const localConversion = convertUtcTimeSlotToLocal(apiSlot.startTime, apiSlot.endTime, utcDateObj);
        displayTime = `${formatTimeWithLuxon(localConversion.startTime)} - ${formatTimeWithLuxon(localConversion.endTime)}`;
      }

      slots.push({
        id: `${slotAvailability.slotId}-slot-${apiSlot.startTime}-${apiSlot.endTime}`,
        time: displayTime,
        price: apiSlot.price.toString(),
        available: apiSlot.availableNumberOfPeople > 0,
        // Keep UTC values as metadata for form submission
        metadata: {
          utcStartTime: apiSlot.startTime,
          utcEndTime: apiSlot.endTime,
          utcDate: slotAvailability.utcDate,
          ...(apiSlot.startAt !== undefined && { startAt: apiSlot.startAt }),
          slotId: slotAvailability.slotId,
          ...(slotAvailability.slotName !== undefined && { slotName: slotAvailability.slotName }),
          ...(apiSlot.acceptsCash !== undefined && { acceptsCash: apiSlot.acceptsCash }),
          ...(apiSlot.acceptsOnline !== undefined && { acceptsOnline: apiSlot.acceptsOnline })
        }
      });
    });
  });

  return slots;
}
